import { localPrice } from "@/api/dtos";
import {
  BriefMonetaryTransaction,
  useAddMonetaryTransaction,
  useDeleteMonetaryTransaction,
  useGetMonetaryTransactionByCategory,
} from "@/api/monetary-transactions";
import * as AF from "@/components/form-items/AbstractForm";
import { currencyOptions } from "@/components/form-fields/InputSelectField";
import { Button } from "@/components/ui/button";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "@/components/ui/custom-dialog";
import { Dialog, DialogHeader, DialogTrigger } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import i18n from "@/i18n";
import { zodResolver } from "@hookform/resolvers/zod";
import { DialogClose } from "@radix-ui/react-dialog";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const monetaryTransactionSchema = z.object({
  title: z.string().nonempty(),
  date: z.string().nonempty(),
  price: z.object({
    amount: z.coerce.number(),
    currency: z.string(),
  }),
});

const AddMonetaryTransaction = ({
  category,
  relatedArtworkId,
}: {
  category: string;
  relatedArtworkId: string;
}) => {
  const [open, setOpen] = useState(false);

  const monetaryTransactionAdder = useAddMonetaryTransaction();

  const form = useForm<z.infer<typeof monetaryTransactionSchema>>({
    resolver: zodResolver(monetaryTransactionSchema),
    defaultValues: {
      title: "",
      date: "",
    },
  });

  const { t } = useTranslation();

  function onSubmit(values: z.infer<typeof monetaryTransactionSchema>) {
    monetaryTransactionAdder.mutate(
      {
        category: category,
        transaction: {
          title: values.title,
          amount: values.price.amount,
          currency: values.price.currency,
          date: values.date,
          relatedArtworkId,
        },
      },
      {
        onSuccess: () => setOpen(false),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          className="flex justify-center p-0 gap-2 w-fit font-semibold text-sm"
          variant={"link"}
        >
          <PlusIcon className="stroke-primary" />{" "}
          {t("suiviFinancierAjouterBouton")}
        </Button>
      </DialogTrigger>
      <CustomDialogContent>
        <Form {...form}>
          <form
            className="flex flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <DialogHeader>
              <CustomDialogTitle>
                {t("suiviFinancierPopupTitre")}
              </CustomDialogTitle>
            </DialogHeader>
            <AF.Col className="p-6">
              <AF.Text
                name="title"
                label={t(`titreSuiviFinancier_${category}`)}
              />
              <AF.Row>
                <AF.Text name="date" label={t("suiviFinancierDate")} />
                <AF.SelectCurrency
                  name="price"
                  label={t("suiviFinancierMontant")}
                />
              </AF.Row>
            </AF.Col>
            <CustomDialogFooter>
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="flex flex-1 bg-white"
                >
                  {t("actionCancel")}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={!form.formState.isValid}
                className="flex flex-1"
              >
                {t("suiviFinancierAjouterBouton")}
              </Button>
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

const MonetaryTransactionCard = ({
  content,
}: {
  content: BriefMonetaryTransaction;
}) => {
  const transactionDeleter = useDeleteMonetaryTransaction();

  function onDelete() {
    transactionDeleter.mutate({ ...content });
  }

  const { currency } = content;

  const currencySymbol = useMemo(
    () => currencyOptions.find((c) => c.value === currency)?.sign ?? currency,
    [currency],
  );

  return (
    <div className="border rounded-md py-3 px-4 text-left flex justify-between hover:bg-gray-50 bg-white transition-colors duration-300">
      <div className="flex flex-col gap-2">
        <div className="flex gap-5 text-sm text-gray-500 font-semibold">
          <div>{content.date}</div>
          <div>.</div>
          <div className="text-black">
            {localPrice(i18n.language, content.amount)} {currencySymbol}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{content.title}</h4>
        </div>
      </div>
      <div className="flex items-center justify-center">
        <Button type="button" variant={"ghost"} onClick={onDelete}>
          <TrashIcon className="text-red-600" />
        </Button>
      </div>
    </div>
  );
};

export const MonetaryTransactionList = (props: {
  category: string;
  relatedArtworkId: string;
}) => {
  const { t } = useTranslation();

  const { data } = useGetMonetaryTransactionByCategory(
    props.relatedArtworkId,
    props.category,
  );

  return (
    <div className="flex flex-col gap-8">
      <h3>{t(`titreSuiviFinancier_${props.category}`)}</h3>
      <div className="flex flex-col gap-4">
        {data?.map((transaction) => (
          <MonetaryTransactionCard content={transaction} key={transaction.id} />
        ))}
      </div>
      <AddMonetaryTransaction {...props} />
    </div>
  );
};
